<template>
  <div class="part-editor">
      <div class="workspace-block">
          <template v-for="tag of tags">
            <div
              :id="part + tag + 'w' + (index ? index : '')"
              :key="tag"
              :class="tag === 'p' ? 'mt-3' : ''"
            >
              <div class="d-flex align-start">
                <div class="flex-grow-1 min-width-0">
                  <Editor
                    should-listen-for-change
                    :value="value[tag]"
                    :placeholder="$t('article.placeholder.' + (tag === 'h' ? 'heading' : 'paragraph'))"
                    :waiting-for-ai="waitingForAi"
                    :words-to-highlight="wordsToHighlight"
                    :is-heading="tag === 'h'"
                    :disabled="disabled"
                    @input="updatePart(tag, $event)"
                  />
                </div>
                <v-expand-x-transition>
                  <ArticlePartExtend
                    v-if="!shareLocked && tag === 'h' && value.h && !value.p"
                    class="heading-extend-btn"
                    :part="part"
                    :uid="value.uid"
                    :loading.sync="loadingExtend"
                    :words-generated-elsewhere="wordsGeneratedInP"
                    :force-prompt="strippedHeading"
                    :type="partExtendFromHeading"
                    hide-extensions
                    lang-path="heading"
                    :disabled="disabled"
                    @wordsGenerated="count => wordsGeneratedInH = count"
                    @generatingExtensionValueId="createWatcherForP"
                    @append="appendToParagraph"
                  />
                </v-expand-x-transition>
              </div>
              <v-expand-transition>
                <ArticlePartExtend
                  v-if="!shareLocked && tag === 'p' && showExtendWithAi && !waitingForAi"
                  :part="part"
                  :uid="value.uid"
                  :loading.sync="loadingExtend"
                  :words-generated-elsewhere="wordsGeneratedInH"
                  :watch-for-extension-value="watchForExtenstionValue"
                  :language-name="languageName"
                  lang-path="paragraph"
                  :disabled="disabled"
                  @wordsGenerated="count => wordsGeneratedInP = count"
                  @append="appendToParagraph"
                />
              </v-expand-transition>
              <div class="d-flex flex-wrap align-center text-caption-primary mx-1 workspace-block-meta">
                <span class="workspace-block-label workspace-block-label--words">
                  {{ $t('article.numOfWords', [wordCount[tag]]) }}
                </span>
                <VerticalDivider class="workspace-block-label workspace-block-label--words" />
                <span class="workspace-block-label">
                  {{ $t('article.label.' + part + tag) }}
                </span>
                <template v-if="part === 'h1' && tag === 'h'">
                  <LucySpark
                    hide-when-done
                    metric="keywordInH1"
                  >
                    <template #before>
                      <VerticalDivider />
                    </template>
                  </LucySpark>
                </template>
                <template v-if="hasInspirations">
                  <VerticalDivider />
                  <ArticlePartInspirationToggle
                    :value="showInspirations"
                    @input="$emit('toggle-inspirations')"
                  />
                </template>
                <template v-if="$te('article.tips.' + part + tag)">
                  <VerticalDivider />
                  <ArticlePartTipToggle v-model="showTips[tag]" />
                </template>
                <template v-if="tag === 'p' && !shareLocked">
                  <VerticalDivider />
                  <ArticlePartExtendToggle
                    v-model="showExtendWithAi"
                    lang-path="paragraph"
                  />
                </template>
                <slot
                  v-if="tag === 'p'"
                  name="paragraphToolbarAppend"
                />
              </div>
              <ArticlePartTip
                :show="showTips[tag]"
                :lang-path="'article.tips.' + part + tag"
              />
            </div>
          </template>
        </div>
      <div v-if="!shareLocked" class="block-remove">
        <ArticlePartRemoveDialog
          :part="part"
          :title="value.h"
          :content="value.p"
          :disabled="disabled"
          @removePart="removePart"
        />
      </div>
    </div>
</template>

<script lang="ts">
    import Vue, { PropType } from 'vue';
    import Editor from '@/components/Editor.vue';
    import { Article, ArticleContentKey, ArticlePartContent } from "@/model/Article";
    import VerticalDivider from "@/components/VerticalDivider.vue";
    import ArticlePartTip from "@/components/article/ArticlePartTip.vue";
    import ArticlePartTipToggle from "@/components/article/ArticlePartTipToggle.vue";
    import ArticlePartInspirationToggle from "@/components/article/ArticlePartInspirationToggle.vue";
    import ArticlePartRemoveDialog from "@/components/article/ArticlePartRemoveDialog.vue";
    import ArticlePartExtendToggle from "@/components/article/ArticlePartExtendToggle.vue";
    import ArticlePartExtend from "@/components/article/ArticlePartExtend.vue";
    import { Store } from "@/plugins/Store";
    import { LocationLanguages } from "@/enum/locations_languages";
    import { stripTagsAndEntities } from "@/utils/string";
    import { OpenAiExpandPartType } from "@/model/OpenAI";
    import LucySpark from '@/components/lucy/LucySpark.vue';

    export default Vue.extend({
        name: 'ArticleContentPartEditor',
        components: {
            ArticlePartExtend,
            ArticlePartExtendToggle,
            ArticlePartRemoveDialog,
            ArticlePartInspirationToggle, ArticlePartTipToggle, ArticlePartTip, VerticalDivider, Editor, LucySpark
        },
        props: {
            part: {
                type: String as PropType<ArticleContentKey>,
                default: () => ''
            },
            index: {
                type: Number,
                default: 0
            },
            hasInspirations: {
                type: Boolean,
                default: false
            },
            showInspirations: {
                type: Boolean,
                default: false
            },
            wordsToHighlight: {
                type: Object as PropType<{ [key: string]: string[] }>,
                default: () => ({})
            },
            disabled: {
                type: Boolean,
                default: false
            },
        },
        // emits: ['remove'],
        data: () => ({
            showExtendWithAi: true,
            watchForExtenstionValue: '',
            wordsGeneratedInH: 0,
            wordsGeneratedInP: 0,
            loadingExtend: false,
            showTips: {
                h: false,
                p: false,
            },
            shouldListedForChange: {
                h: true,
                p: true
            },
            partExtendFromHeading: OpenAiExpandPartType.FROM_HEADING,
            tags: ['h', 'p'] as const
        }),
        computed: {
            article: function (): Article {
                return this.$store.getters.article;
            },
            value: function (): ArticlePartContent {
                const storePart = this.$store.getters.article[this.part];
                if (!storePart[this.index]) {
                    storePart[this.index] = {h: '', p: ''};
                }
                return storePart[this.index];
            },
            wordCount: function (): { h: number, p: number } {
                return this.$store.getters.articleWordCount[this.part][this.index] || { h: 0, p: 0 };
            },
            waitingForAi: function (): boolean {
                const subpart = this.$store.getters.articleAiLoading[this.part][this.index];
                return subpart?.h || subpart?.p || false;
            },
            shareLocked(): boolean {
                return !!this.$store.getters.sharePreview;
            },
            languageName: function (): string {
                const languageName = "English";
                for (const location of LocationLanguages) {
                    for (const language of location.available_languages) {
                        if (language.language_code === this.article.language_code) {
                            return language.language_name;
                        }
                    }
                }
                return languageName;
            },
            strippedHeading: function (): string {
                return stripTagsAndEntities(this.value.h);
            },
        },
        methods: {
            updatePart: function (tag: 'h' | 'p', value: string): void {
                // CKEditor's paragraph plugin wraps a plain heading as <p>…</p>.
                // Keep the stored heading as plain text. Do not rewrite a heading
                // that was already saved with tags (that would change an article on open).
                if (tag === 'h' && value) {
                    const current = String(this.value.h || '');
                    const plainNext = stripTagsAndEntities(value);
                    const plainCurrent = stripTagsAndEntities(current);
                    if (plainNext && plainNext === plainCurrent && /<\s*p\b/i.test(value) && !/<\s*p\b/i.test(current)) {
                        value = plainNext;
                    }
                }
                this.$store.commit('setArticleContent', {
                    contentPart: this.part,
                    index: this.index,
                    contentKey: tag,
                    value: value
                });
            },
            createWatcherForP: function (valueId: string) {
                this.watchForExtenstionValue = valueId;
            },
            removePart: function (): void {
                this.$emit('remove');
            },
            appendToParagraph: function (text: string): void {
                //this.shouldListedForChange.p = true;
                Store.commit('setArticleContent', {
                    contentPart: this.part,
                    index: this.index,
                    contentKey: 'p',
                    value: this.value.p + (this.value.p ? '<br><br>' : '') + text
                });
                /*
                this.$nextTick(() => {
                    this.shouldListedForChange.p = false;
                });
                */
            }
        }
    });
</script>

<style lang="sass" scoped>
.part-editor
  position: relative
  width: 100%

.min-width-0
  min-width: 0

.heading-extend-btn
  flex: 0 0 auto
  margin-left: 12px
  padding-top: 2px
  max-width: 280px

.block-remove
  position: absolute
  top: 4px
  right: -40px
  z-index: 2

  ::v-deep .side-button
    margin-left: 0 !important
</style>
